import type { HelpContent } from './types';

export const utilisationHelp: HelpContent = {
  pageTitle: 'Utilisation',
  description: 'Monitor resource utilisation across your organisation — see who is under-utilised, on target, or over-allocated at a glance.',

  functionality: {
    overview: 'The Utilisation page gives you a real-time view of how your resources are being used across your project portfolio. It shows each resource\'s utilisation percentage and flags whether they are under-utilised, on target, or over-allocated, helping you balance workloads and plan capacity effectively.',

    keyFeatures: [
      {
        label: 'Utilisation summary',
        detail: 'KPI cards show average utilisation across the organisation, plus the count of resources who are under-utilised, on target, or over-allocated.',
      },
      {
        label: 'Per-resource utilisation',
        detail: 'Each resource is listed with their utilisation percentage and a status badge — Under-Utilised, On Target, or Over-Allocated.',
      },
      {
        label: 'Visual utilisation bar',
        detail: 'A progress bar for each resource gives a quick visual indication of how close they are to their target utilisation.',
      },
      {
        label: 'Status-based colour coding',
        detail: 'Resources are colour-coded by utilisation status, making it easy to spot capacity issues at a glance.',
      },
    ],

    sectionsAndFields: [
      {
        name: 'KPI summary cards',
        description: 'Displays four metrics: average utilisation percentage, count of under-utilised resources, count of on-target resources, and count of over-allocated resources.',
      },
      {
        name: 'Utilisation table',
        description: 'Lists all resources with their name, role, utilisation percentage, utilisation bar, and status badge.',
      },
      {
        name: 'Utilisation percentage',
        description: 'The percentage of a resource\'s available capacity that is currently allocated to project work.',
      },
      {
        name: 'Utilisation bar',
        description: 'A visual progress bar showing utilisation as a proportion of the target. The bar colour changes based on the resource\'s utilisation status.',
      },
      {
        name: 'Status badge',
        description: 'Indicates the utilisation status of the resource — Under-Utilised (yellow), On Target (green), or Over-Allocated (red).',
      },
    ],

    useCases: [
      'Identifying under-utilised resources who could be allocated to additional project work.',
      'Spotting over-allocated resources before they become a delivery or wellbeing risk.',
      'Reviewing overall organisational capacity before taking on new projects.',
      'Balancing workloads across the team.',
    ],
  },

  userGuide: {
    gettingStarted: 'The Utilisation page loads with all resources and their current utilisation data. Use the KPI cards for a summary, then review individual resources in the table.',
    steps: [
      {
        title: 'How do I find over-allocated resources?',
        items: [
          { text: 'Look for the red "Over-Allocated" badge in the utilisation table.' },
          { text: 'The "Over-Allocated" KPI card at the top shows the count of affected resources.' },
          { text: 'Review their current project allocations in the Resource Directory to understand why they are over-allocated.' },
        ],
      },
      {
        title: 'How do I find resources with spare capacity?',
        items: [
          { text: 'Look for the yellow "Under-Utilised" badge in the utilisation table.' },
          { text: 'The "Under-Utilised" KPI card shows the count of resources with spare capacity.' },
          { text: 'Click through to the Resource Directory to review their current allocations and availability.' },
        ],
      },
    ],
    proTips: [
      'Use this page alongside the Resource Directory to move from identifying a utilisation issue to understanding the underlying allocations causing it.',
      'Review utilisation at the start of each planning cycle to identify capacity before committing to new project work.',
    ],
  },

  faq: [
    {
      question: 'How is utilisation calculated?',
      answer: 'Utilisation is calculated as the percentage of a resource\'s available capacity that is currently allocated to project work. The target utilisation rate is configured by an Administrator in Settings.',
    },
    {
      question: 'What does "On Target" mean?',
      answer: '"On Target" means the resource\'s utilisation falls within the acceptable range defined by your organisation\'s target utilisation settings.',
    },
    {
      question: 'Can I update resource allocations from this page?',
      answer: 'No. The Utilisation page is read-only. To update resource allocations, navigate to the relevant project\'s Resourcing tab in Project Details.',
    },
    {
      question: 'Why is a resource not showing in the utilisation list?',
      answer: 'Only active resources are shown. If a resource is missing, check that they are set to active in the Resource Directory.',
    },
  ],

  relatedPages: [
    { label: 'Resource Directory', path: '/directory' },
    { label: 'Roles & Rates', path: '/roles-rates' },
  ],
};
